/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { SpecialInstruction } from './SpecialInstruction';
import {
    SpecialInstructionFromJSON,
    SpecialInstructionFromJSONTyped,
    SpecialInstructionToJSON,
    SpecialInstructionToJSONTyped,
} from './SpecialInstruction';
import type { Dimensions } from './Dimensions';
import {
    DimensionsFromJSON,
    DimensionsFromJSONTyped,
    DimensionsToJSON,
    DimensionsToJSONTyped,
} from './Dimensions';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
    DocumentToJSONTyped,
} from './Document';
import type { ConsignmentDetail } from './ConsignmentDetail';
import {
    ConsignmentDetailFromJSON,
    ConsignmentDetailFromJSONTyped,
    ConsignmentDetailToJSON,
    ConsignmentDetailToJSONTyped,
} from './ConsignmentDetail';
import type { Contact } from './Contact';
import {
    ContactFromJSON,
    ContactFromJSONTyped,
    ContactToJSON,
    ContactToJSONTyped,
} from './Contact';
import type { TimeRange } from './TimeRange';
import {
    TimeRangeFromJSON,
    TimeRangeFromJSONTyped,
    TimeRangeToJSON,
    TimeRangeToJSONTyped,
} from './TimeRange';
import type { SpecialCharacteristic } from './SpecialCharacteristic';
import {
    SpecialCharacteristicFromJSON,
    SpecialCharacteristicFromJSONTyped,
    SpecialCharacteristicToJSON,
    SpecialCharacteristicToJSONTyped,
} from './SpecialCharacteristic';
import type { Party } from './Party';
import {
    PartyFromJSON,
    PartyFromJSONTyped,
    PartyToJSON,
    PartyToJSONTyped,
} from './Party';
import type { ExternalReference } from './ExternalReference';
import {
    ExternalReferenceFromJSON,
    ExternalReferenceFromJSONTyped,
    ExternalReferenceToJSON,
    ExternalReferenceToJSONTyped,
} from './ExternalReference';
import type { Value } from './Value';
import {
    ValueFromJSON,
    ValueFromJSONTyped,
    ValueToJSON,
    ValueToJSONTyped,
} from './Value';
import type { Person } from './Person';
import {
    PersonFromJSON,
    PersonFromJSONTyped,
    PersonToJSON,
    PersonToJSONTyped,
} from './Person';
import type { Link } from './Link';
import {
    LinkFromJSON,
    LinkFromJSONTyped,
    LinkToJSON,
    LinkToJSONTyped,
} from './Link';
import type { Location } from './Location';
import {
    LocationFromJSON,
    LocationFromJSONTyped,
    LocationToJSON,
    LocationToJSONTyped,
} from './Location';

/**
 * The consignment is a description of the goods that the seller will deliver to the buyer. It can be related to one or many customer order lines from the same or different customer orders, but it is always for a maximum of one source and one destination. If the buyer is collecting the goods, the final location of the consignment is the origin location. In logistics, the consignment is linked to at least one service that is responsible for the end-to-end delivery to the final consignee. If the information needs to be shared with other service providers in the logistics chain, the related services are added to the consignment. Access rights to the consignment are given by its owner.
 * @export
 * @interface Consignment
 */
export interface Consignment {
    /**
     * Actual start date and time of the delivery of the consignment.
     * @type {Date}
     * @memberof Consignment
     */
    actualStartDateTime?: Date;
    /**
     * Actual end date and time of the delivery of the consignment.
     * @type {Date}
     * @memberof Consignment
     */
    actualEndDateTime?: Date;
    /**
     * Party receiving the goods at final destination.
     * @type {Party}
     * @memberof Consignment
     */
    consignee?: Party;
    /**
     * The consignment details describe the content of the delivery from the origin location to the final delivery location, related to the customer orders between the vendor and the buyer of the goods.
     * @type {Array<ConsignmentDetail>}
     * @memberof Consignment
     */
    consignmentDetails?: Array<ConsignmentDetail>;
    /**
     * The consignor is the party in charge of defining and dispatching the batch of goods delivered in the consignment. They can delegate the packaging and loading operations, but they remain accountable to the buyer.
     * @type {Party}
     * @memberof Consignment
     */
    consignor?: Party;
    /**
     * List of the contacts of the consignment. Can be persons or teams to be contacted about the consignment.
     * @type {Array<Contact>}
     * @memberof Consignment
     */
    contacts?: Array<Contact>;
    /**
     * Total dimensions of the consignment whatever the number of pieces.
     * @type {Dimensions}
     * @memberof Consignment
     */
    dimensions?: Dimensions;
    /**
     * This is a list of documents related to the consignment, such as a packing list, bill of lading, or other relevant paperwork.
     * @type {Array<Document>}
     * @memberof Consignment
     */
    documents?: Array<Document>;
    /**
     * Requested start time range for the delivery of the consignment.
     * @type {TimeRange}
     * @memberof Consignment
     */
    requestedStartTimeRange?: TimeRange;
    /**
     * Requested end time range for the delivery of the consignment.
     * @type {TimeRange}
     * @memberof Consignment
     */
    requestedEndTimeRange?: TimeRange;
    /**
     * Planned start time range for the delivery of the consignment.
     * @type {TimeRange}
     * @memberof Consignment
     */
    plannedStartTimeRange?: TimeRange;
    /**
     * Planned end time range for the delivery of the consignment.
     * @type {TimeRange}
     * @memberof Consignment
     */
    plannedEndTimeRange?: TimeRange;
    /**
     * Reference planned start time range for the delivery of the consignment.
     * @type {TimeRange}
     * @memberof Consignment
     */
    referencePlannedStartTimeRange?: TimeRange;
    /**
     * Reference planned end time range for the delivery of the consignment.
     * @type {TimeRange}
     * @memberof Consignment
     */
    referencePlannedEndTimeRange?: TimeRange;
    /**
     * External references of the consignment should be used when they are defined in a legacy system registering the consignment under another reference identifier other than the URI. This is useful for searching the consignment using the ID from the legacy app.
     *                                Common external reference types include 'SELLER_ID', 'BUYER_ID', and 'PROVIDER_ID'.
     * @type {Array<ExternalReference>}
     * @memberof Consignment
     */
    externalReferences?: Array<ExternalReference>;
    /**
     * The final location of the consignment is the destination where the goods are being delivered.
     * @type {Location}
     * @memberof Consignment
     */
    finalLocation?: Location;
    /**
     * The origin location of the consignment is the place where the goods are originating from or being shipped from.
     * @type {Location}
     * @memberof Consignment
     */
    originLocation?: Location;
    /**
     * The recipient is the party formally accepting the delivery of the goods.
     * @type {Person}
     * @memberof Consignment
     */
    recipient?: Person;
    /**
     * List of customer service requests.
     * @type {Array<Link>}
     * @memberof Consignment
     */
    serviceRequests?: Array<Link>;
    /**
     * List of the services related to the consignment and having access to its data attributes. The first service listed is responsible for end-to-end delivery of the consignment. Other services may have an access to the details of the consignment, such as custom brokerage information.
     * @type {Array<Link>}
     * @memberof Consignment
     */
    services?: Array<Link>;
    /**
     * Special characteristics applied to the consignment and to be shared with a provider.
     * @type {Array<SpecialCharacteristic>}
     * @memberof Consignment
     */
    specialCharacteristics?: Array<SpecialCharacteristic>;
    /**
     * These are special characteristics of the consignment that should be shared with the provider.
     * @type {Array<SpecialInstruction>}
     * @memberof Consignment
     */
    specialInstructions?: Array<SpecialInstruction>;
    /**
     * Total volume of the consignment.
     * @type {Value}
     * @memberof Consignment
     */
    volume?: Value;
    /**
     * Total weight of the consignment.
     * @type {Value}
     * @memberof Consignment
     */
    weight?: Value;
}

/**
 * Check if a given object implements the Consignment interface.
 */
export function instanceOfConsignment(value: object): value is Consignment {
    return true;
}

export function ConsignmentFromJSON(json: any): Consignment {
    return ConsignmentFromJSONTyped(json, false);
}

export function ConsignmentFromJSONTyped(json: any, ignoreDiscriminator: boolean): Consignment {
    if (json == null) {
        return json;
    }
    return {
        
        'actualStartDateTime': json['actualStartDateTime'] == null ? undefined : (new Date(json['actualStartDateTime'])),
        'actualEndDateTime': json['actualEndDateTime'] == null ? undefined : (new Date(json['actualEndDateTime'])),
        'consignee': json['consignee'] == null ? undefined : PartyFromJSON(json['consignee']),
        'consignmentDetails': json['consignmentDetails'] == null ? undefined : ((json['consignmentDetails'] as Array<any>).map(ConsignmentDetailFromJSON)),
        'consignor': json['consignor'] == null ? undefined : PartyFromJSON(json['consignor']),
        'contacts': json['contacts'] == null ? undefined : ((json['contacts'] as Array<any>).map(ContactFromJSON)),
        'dimensions': json['dimensions'] == null ? undefined : DimensionsFromJSON(json['dimensions']),
        'documents': json['documents'] == null ? undefined : ((json['documents'] as Array<any>).map(DocumentFromJSON)),
        'requestedStartTimeRange': json['requestedStartTimeRange'] == null ? undefined : TimeRangeFromJSON(json['requestedStartTimeRange']),
        'requestedEndTimeRange': json['requestedEndTimeRange'] == null ? undefined : TimeRangeFromJSON(json['requestedEndTimeRange']),
        'plannedStartTimeRange': json['plannedStartTimeRange'] == null ? undefined : TimeRangeFromJSON(json['plannedStartTimeRange']),
        'plannedEndTimeRange': json['plannedEndTimeRange'] == null ? undefined : TimeRangeFromJSON(json['plannedEndTimeRange']),
        'referencePlannedStartTimeRange': json['referencePlannedStartTimeRange'] == null ? undefined : TimeRangeFromJSON(json['referencePlannedStartTimeRange']),
        'referencePlannedEndTimeRange': json['referencePlannedEndTimeRange'] == null ? undefined : TimeRangeFromJSON(json['referencePlannedEndTimeRange']),
        'externalReferences': json['externalReferences'] == null ? undefined : ((json['externalReferences'] as Array<any>).map(ExternalReferenceFromJSON)),
        'finalLocation': json['finalLocation'] == null ? undefined : LocationFromJSON(json['finalLocation']),
        'originLocation': json['originLocation'] == null ? undefined : LocationFromJSON(json['originLocation']),
        'recipient': json['recipient'] == null ? undefined : PersonFromJSON(json['recipient']),
        'serviceRequests': json['serviceRequests'] == null ? undefined : ((json['serviceRequests'] as Array<any>).map(LinkFromJSON)),
        'services': json['services'] == null ? undefined : ((json['services'] as Array<any>).map(LinkFromJSON)),
        'specialCharacteristics': json['specialCharacteristics'] == null ? undefined : ((json['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicFromJSON)),
        'specialInstructions': json['specialInstructions'] == null ? undefined : ((json['specialInstructions'] as Array<any>).map(SpecialInstructionFromJSON)),
        'volume': json['volume'] == null ? undefined : ValueFromJSON(json['volume']),
        'weight': json['weight'] == null ? undefined : ValueFromJSON(json['weight']),
    };
}

export function ConsignmentToJSON(json: any): Consignment {
    return ConsignmentToJSONTyped(json, false);
}

export function ConsignmentToJSONTyped(value?: Consignment | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'actualStartDateTime': value['actualStartDateTime'] == null ? value['actualStartDateTime'] : value['actualStartDateTime'].toISOString(),
        'actualEndDateTime': value['actualEndDateTime'] == null ? value['actualEndDateTime'] : value['actualEndDateTime'].toISOString(),
        'consignee': PartyToJSON(value['consignee']),
        'consignmentDetails': value['consignmentDetails'] == null ? undefined : ((value['consignmentDetails'] as Array<any>).map(ConsignmentDetailToJSON)),
        'consignor': PartyToJSON(value['consignor']),
        'contacts': value['contacts'] == null ? undefined : ((value['contacts'] as Array<any>).map(ContactToJSON)),
        'dimensions': DimensionsToJSON(value['dimensions']),
        'documents': value['documents'] == null ? undefined : ((value['documents'] as Array<any>).map(DocumentToJSON)),
        'requestedStartTimeRange': TimeRangeToJSON(value['requestedStartTimeRange']),
        'requestedEndTimeRange': TimeRangeToJSON(value['requestedEndTimeRange']),
        'plannedStartTimeRange': TimeRangeToJSON(value['plannedStartTimeRange']),
        'plannedEndTimeRange': TimeRangeToJSON(value['plannedEndTimeRange']),
        'referencePlannedStartTimeRange': TimeRangeToJSON(value['referencePlannedStartTimeRange']),
        'referencePlannedEndTimeRange': TimeRangeToJSON(value['referencePlannedEndTimeRange']),
        'externalReferences': value['externalReferences'] == null ? undefined : ((value['externalReferences'] as Array<any>).map(ExternalReferenceToJSON)),
        'finalLocation': LocationToJSON(value['finalLocation']),
        'originLocation': LocationToJSON(value['originLocation']),
        'recipient': PersonToJSON(value['recipient']),
        'serviceRequests': value['serviceRequests'] == null ? undefined : ((value['serviceRequests'] as Array<any>).map(LinkToJSON)),
        'services': value['services'] == null ? undefined : ((value['services'] as Array<any>).map(LinkToJSON)),
        'specialCharacteristics': value['specialCharacteristics'] == null ? undefined : ((value['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicToJSON)),
        'specialInstructions': value['specialInstructions'] == null ? undefined : ((value['specialInstructions'] as Array<any>).map(SpecialInstructionToJSON)),
        'volume': ValueToJSON(value['volume']),
        'weight': ValueToJSON(value['weight']),
    };
}

